/**
 * Canonical Datadog facet for desktop session-pipeline breadcrumbs.
 *
 * Filter Datadog with `@session_source_type:<value>` for one pipeline, or
 * `@session_origin:<remote|native>` for the coarser Remote-vs-desktop split.
 *
 * The values mix camelCase and snake_case because two of them already ship on
 * `main` and are indexed in live Datadog queries: `localRecording` from
 * `src/tauri/online-chunk-upload/listeners.ts`, and `chronicleLiveRecording`
 * from the Rust `chronicle:observability` payload. Renaming either would break
 * existing dashboards, so the new values are added alongside rather than
 * normalising the set — which is why `session_origin` exists as a separate,
 * regularly-valued field instead of a prefix convention on this one.
 */
export const SESSION_SOURCE_TYPE = {
  /** Online desktop-mic / native capture. */
  LocalRecording: 'localRecording',
  /**
   * Online Chronicle Remote live BLE stream. Emitted by Rust and forwarded
   * verbatim through `use-chronicle-observability-listener`, so it has no
   * TypeScript caller — it is listed here to keep the facet's value set
   * discoverable from one place.
   */
  ChronicleLiveRecording: 'chronicleLiveRecording',
  /** Chronicle offline bulk sync (device → desktop upload). */
  BulkSync: 'bulk_sync',
  /** Desktop offline-recording sync (mic cache → upload). */
  OfflineRecording: 'offline_recording',
} as const;

export type SessionSourceType =
  (typeof SESSION_SOURCE_TYPE)[keyof typeof SESSION_SOURCE_TYPE];

/**
 * Which machine captured the audio, independent of online/offline pipeline.
 * Answers "Heidi Remote or the desktop's own mic?" in one term, so the split
 * does not have to be spelled as an OR over `session_source_type` values.
 */
export const SESSION_ORIGIN = {
  /** Heidi Remote hardware — live BLE stream or bulk-synced device recording. */
  Remote: 'remote',
  /** The desktop machine's own capture — online mic or offline mic cache. */
  Native: 'native',
} as const;

export type SessionOrigin = (typeof SESSION_ORIGIN)[keyof typeof SESSION_ORIGIN];

/**
 * `session_origin` is derived from `session_source_type` in
 * `log-hardware-datadog-event`, never stamped by hand at a call site — one
 * mapping table means the two facets can never disagree in the log index.
 */
export const SESSION_ORIGIN_BY_SOURCE_TYPE = {
  [SESSION_SOURCE_TYPE.LocalRecording]: SESSION_ORIGIN.Native,
  [SESSION_SOURCE_TYPE.ChronicleLiveRecording]: SESSION_ORIGIN.Remote,
  [SESSION_SOURCE_TYPE.BulkSync]: SESSION_ORIGIN.Remote,
  [SESSION_SOURCE_TYPE.OfflineRecording]: SESSION_ORIGIN.Native,
} as const satisfies Record<SessionSourceType, SessionOrigin>;

export function sessionOriginForSourceType(
  sourceType: string | undefined
): SessionOrigin | undefined {
  if (sourceType === undefined) return undefined;
  return (SESSION_ORIGIN_BY_SOURCE_TYPE as Record<string, SessionOrigin | undefined>)[
    sourceType
  ];
}
